import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="text-3xl text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Privacy Policy
            </CardTitle>
            <p className="text-white/80 text-sm mt-2">Last Updated: October 24, 2025</p>
          </CardHeader>
          <CardContent className="text-white space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Information We Collect</h2>
              <p className="text-white/90 leading-relaxed mb-3">
                We collect the following types of information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/90">
                <li><strong>Account Information:</strong> Name, email address, and profile picture (via Google OAuth)</li>
                <li><strong>Search Data:</strong> Chemical names and analyses you search for</li>
                <li><strong>Usage Data:</strong> Search history, bookmarked chemicals, and interaction patterns</li>
                <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store payment card details)</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information, and access logs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. How We Use Your Information</h2>
              <p className="text-white/90 leading-relaxed mb-3">
                We use your information for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/90">
                <li>Providing and maintaining the Service</li>
                <li>Processing chemical compatibility analyses</li>
                <li>Managing your account and subscriptions</li>
                <li>Saving your search history and bookmarks</li>
                <li>Sending service-related communications</li>
                <li>Improving and optimizing the Service</li>
                <li>Detecting and preventing fraud or abuse</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Data Storage and Security</h2>
              <p className="text-white/90 leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information. Your data is stored securely using industry-standard encryption. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Third-Party Services</h2>
              <p className="text-white/90 leading-relaxed mb-3">
                We use the following third-party services:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/90">
                <li><strong>Google OAuth:</strong> For authentication (subject to Google's Privacy Policy)</li>
                <li><strong>Stripe:</strong> For payment processing (subject to Stripe's Privacy Policy)</li>
                <li><strong>Anthropic Claude:</strong> For AI-powered chemical analysis</li>
              </ul>
              <p className="text-white/90 leading-relaxed mt-3">
                These services may collect data according to their own privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Data Sharing</h2>
              <p className="text-white/90 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/90">
                <li>With service providers who assist in operating the Service</li>
                <li>When required by law or legal process</li>
                <li>To protect our rights, property, or safety</li>
                <li>With your explicit consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Your Rights</h2>
              <p className="text-white/90 leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/90">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Export your data</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent for data processing</li>
              </ul>
              <p className="text-white/90 leading-relaxed mt-3">
                To exercise these rights, contact us at{' '}
                <a href="mailto:info@corrosioncheck.com" className="text-blue-300 hover:text-blue-200 underline">
                  info@corrosioncheck.com
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Cookies and Tracking</h2>
              <p className="text-white/90 leading-relaxed">
                We use cookies and similar tracking technologies to maintain your session and improve user experience. You can control cookie preferences through your browser settings, but disabling cookies may affect Service functionality.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Data Retention</h2>
              <p className="text-white/90 leading-relaxed">
                We retain your personal information for as long as necessary to provide the Service and comply with legal obligations. You may request deletion of your account and associated data at any time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Children's Privacy</h2>
              <p className="text-white/90 leading-relaxed">
                The Service is not intended for users under 18 years of age. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. International Users</h2>
              <p className="text-white/90 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. By using the Service, you consent to such transfers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Changes to Privacy Policy</h2>
              <p className="text-white/90 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify users of significant changes via email or through the Service. Continued use after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Contact Us</h2>
              <p className="text-white/90 leading-relaxed">
                For questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-white/90 mt-2">
                <a href="mailto:info@corrosioncheck.com" className="text-blue-300 hover:text-blue-200 underline">
                  info@corrosioncheck.com
                </a>
              </p>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
